import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceDot, BarChart, Bar, Legend } from 'recharts';
import { T, MONO } from './poyais-ui/theme';
import { Card, Grid, Button } from './poyais-ui/primitives';
import { DateFilterBox } from './poyais-ui/DateFilter';
import { useLang, addTranslations } from './i18n';
import PoyaisSpinner from './PoyaisSpinner';

addTranslations({
  'Genel Bakış': 'Overview', 'Satışlar': 'Sales', 'Ürünler': 'Products', 'Finans': 'Finance',
  'Toplam Ciro': 'Total Revenue', 'Sipariş Adedi': 'Orders', 'Ortalama Sepet': 'Average Basket', 'Kâr Marjı': 'Profit Margin',
  'vs. önceki dönem': 'vs. previous period', 'Ürün maliyeti girilince hesaplanır': 'Calculated once product cost is entered',
  'Satış Ciro Trendi': 'Sales Revenue Trend', 'Seçili tarih aralığında günlük ciro değişimi.': 'Daily revenue in the selected range.',
  'Zirve': 'Peak', 'Dip': 'Low', 'Yükselişte': 'Rising', 'Düşüşte': 'Falling', 'Ciro': 'Revenue',
  'Kanal Dağılımı': 'Channel Breakdown', 'Kanal': 'Channel', 'Sipariş': 'Orders', 'Pay': 'Share', 'Değişim': 'Change',
  'En Çok Gelir Getiren Ürünler': 'Top Revenue Products', 'Ürün': 'Product', 'Adet': 'Qty',
  'Günlük Ciro (Kanal Bazında)': 'Daily Revenue by Channel',
  'Reklam Harcaması': 'Ad Spend', 'Reklam Payı': 'Ad Share', 'Toplam ROAS': 'Blended ROAS', 'İade Edilen Ciro': 'Refunded Revenue',
  'Net Ciro': 'Net Revenue', 'Ürün Maliyeti': 'Product Cost', 'Brüt Kâr': 'Gross Profit', 'Reklam Sonrası Kâr': 'Profit After Ads',
  'Ortalama ürün maliyeti (% ciro)': 'Average product cost (% of revenue)', 'Kaydet': 'Save',
  'Maliyet oranını gir, kâr marjı otomatik hesaplansın.': 'Enter a cost rate and the margin is calculated automatically.',
  'Veri kaynağı': 'Data source', 'Son güncelleme': 'Last update',
  'Bu müşteri için sipariş verisi yok. Sipariş kaynağı (Pixa, Shopify ya da dosya) bağlanınca burada görünür.': 'No order data for this client yet. It appears once an order source (Pixa, Shopify or file) is connected.',
  'Veri alınamadı.': 'Could not load data.', 'İptal': 'Cancelled', 'Komisyon': 'Commission', 'Kargo': 'Shipping',
});

const API = '/api/v1';
const TABS = ['Genel Bakış', 'Satışlar', 'Ürünler', 'Finans'] as const;
const CH_COLORS: Record<string, string> = { Shopify: '#2F9E6E', Trendyol: '#F27A1A', Boyner: '#2B6CF0', Hepsiburada: '#E4572E' };
const FALLBACK = ['#8B5CF6', '#14B8A6', '#E0A030', '#64748B'];

const tl = (v: number | null | undefined) => v == null ? '—' : '₺' + Math.round(v).toLocaleString('tr-TR');
const tlK = (v: number) => v >= 1e6 ? `₺${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `₺${(v / 1e3).toFixed(1)}K` : `₺${Math.round(v)}`;
const tl2 = (v: number) => '₺' + v.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const pct = (v: number | null | undefined) => v == null ? null : `${v >= 0 ? '+' : ''}${v.toFixed(2).replace('.', ',')}%`;
const dayLabel = (d: string) => new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

function Delta({ v, suffix }: { v: number | null | undefined; suffix?: string }) {
  if (v == null) return <div style={{ fontSize: 12.5, color: T.textDim, fontWeight: 600 }}>—</div>;
  const up = v >= 0;
  return <div style={{ fontSize: 12.5, fontWeight: 700, color: up ? T.greenText : T.redText }}>{up ? '↑' : '↓'} {pct(v)} {suffix}</div>;
}

function Kpi({ label, value, color, icon, children }: { label: string; value: string; color: string; icon: string; children: React.ReactNode }) {
  return (
    <Card pad={20} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, minHeight: 124 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
        <div style={{ fontSize: 12.5, color: T.textMid, fontWeight: 600 }}>{label}</div>
        <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.02em', color: T.text, fontFamily: MONO }}>{value}</div>
        {children}
      </div>
      <span style={{ width: 38, height: 38, borderRadius: 11, background: color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{icon.split('|').map((d, i) => <path key={i} d={d} />)}</svg>
      </span>
    </Card>
  );
}

const ICONS = {
  revenue: 'M3 17l6-6 4 4 8-8|M15 7h6v6',
  orders: 'M3 4h2l2.4 10.5a1.5 1.5 0 001.5 1.2h8a1.5 1.5 0 001.5-1.1L20 8H6',
  basket: 'M12 3l8 4.5v9L12 21l-8-4.5v-9z|M12 12l8-4.5|M12 12v9|M12 12L4 7.5',
  margin: 'M3 6h18v12H3z|M3 10h18|M7 15h3',
};

function TrendChart({ d, channelKeys }: { d: any; channelKeys?: string[] }) {
  const { t } = useLang();
  const data = d.daily as any[];
  const up = (d.trend_pct ?? 0) >= 0;
  return (
    <Card pad={22}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontSize: 19, fontWeight: 800, color: T.text, letterSpacing: '-0.02em' }}>{channelKeys ? t('Günlük Ciro (Kanal Bazında)') : t('Satış Ciro Trendi')}</div>
          <div style={{ fontSize: 13, color: T.textMid, marginTop: 4 }}>{t('Seçili tarih aralığında günlük ciro değişimi.')}</div>
        </div>
        {d.trend_pct != null && !channelKeys && (
          <span style={{ padding: '7px 14px', borderRadius: 999, fontSize: 13, fontWeight: 700, background: up ? 'var(--py-green-tint)' : 'var(--py-red-tint, #FDECEC)', color: up ? T.greenText : T.redText, border: `1px solid ${up ? T.greenText : T.redText}33` }}>
            {up ? '↑' : '↓'} {up ? t('Yükselişte') : t('Düşüşte')} {pct(d.trend_pct)}
          </span>
        )}
      </div>
      <div style={{ height: 330, marginTop: 18 }}>
        <ResponsiveContainer width="100%" height="100%">
          {channelKeys ? (
            <BarChart data={data} margin={{ top: 10, right: 12, left: 4, bottom: 0 }}>
              <CartesianGrid stroke="var(--py-border)" vertical={false} />
              <XAxis dataKey="date" tickFormatter={dayLabel} tick={{ fontSize: 11, fill: 'var(--py-text-dim)' }} minTickGap={28} />
              <YAxis tickFormatter={tlK} tick={{ fontSize: 11, fill: 'var(--py-text-dim)' }} width={56} />
              <Tooltip formatter={(v: any) => tl(Number(v))} labelFormatter={(l: any) => dayLabel(String(l))} />
              <Legend />
              {channelKeys.map((k, i) => <Bar key={k} dataKey={k} stackId="a" fill={CH_COLORS[k] || FALLBACK[i % FALLBACK.length]} />)}
            </BarChart>
          ) : (
            <AreaChart data={data} margin={{ top: 22, right: 18, left: 4, bottom: 0 }}>
              <defs><linearGradient id="sbFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2563EB" stopOpacity={0.18} /><stop offset="100%" stopColor="#2563EB" stopOpacity={0.04} /></linearGradient></defs>
              <CartesianGrid stroke="var(--py-border)" vertical={false} />
              <XAxis dataKey="date" tickFormatter={dayLabel} tick={{ fontSize: 11, fill: 'var(--py-text-dim)' }} minTickGap={36} />
              <YAxis tickFormatter={tlK} tick={{ fontSize: 11, fill: 'var(--py-text-dim)' }} width={56} />
              <Tooltip formatter={(v: any) => [tl(Number(v)), t('Ciro')]} labelFormatter={(l: any) => dayLabel(String(l))} />
              <Area type="monotone" dataKey="revenue" stroke="#2563EB" strokeWidth={2.4} fill="url(#sbFill)" dot={{ r: 3, fill: '#2563EB', strokeWidth: 0 }} />
              {d.peak && <ReferenceDot x={d.peak.date} y={d.peak.revenue} r={5} fill="#2563EB" stroke="#fff" label={{ value: t('Zirve'), position: 'top', fontSize: 11, fontWeight: 700, fill: 'var(--py-text)' }} />}
              {d.dip && <ReferenceDot x={d.dip.date} y={d.dip.revenue} r={5} fill="#2563EB" stroke="#fff" label={{ value: t('Dip'), position: 'bottom', fontSize: 11, fontWeight: 700, fill: 'var(--py-text)' }} />}
            </AreaChart>
          )}
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

function Th({ children, right }: { children: React.ReactNode; right?: boolean }) {
  return <th style={{ textAlign: right ? 'right' : 'left', padding: '10px 12px', fontSize: 11, fontWeight: 700, color: T.textDim, textTransform: 'uppercase', letterSpacing: '.05em', borderBottom: `1px solid ${T.border}` }}>{children}</th>;
}
const td = (right?: boolean): React.CSSProperties => ({ padding: '11px 12px', fontSize: 13, color: T.text, textAlign: right ? 'right' : 'left', borderBottom: `1px solid ${T.border}`, fontFamily: right ? MONO : undefined });

function ChannelsTable({ d }: { d: any }) {
  const { t } = useLang();
  return (
    <Card pad={22}>
      <div style={{ fontSize: 17, fontWeight: 800, color: T.text, marginBottom: 12 }}>{t('Kanal Dağılımı')}</div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><Th>{t('Kanal')}</Th><Th right>{t('Ciro')}</Th><Th right>{t('Sipariş')}</Th><Th right>{t('Ortalama Sepet')}</Th><Th right>{t('Pay')}</Th><Th right>{t('Değişim')}</Th></tr></thead>
          <tbody>
            {d.channels.map((c: any, i: number) => (
              <tr key={c.key}>
                <td style={td()}><span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 3, background: CH_COLORS[c.key] || FALLBACK[i % FALLBACK.length], marginRight: 8 }} />{c.platform}</td>
                <td style={td(true)}>{tl(c.revenue)}</td><td style={td(true)}>{c.orders}</td><td style={td(true)}>{tl(c.avg_basket)}</td>
                <td style={td(true)}>%{c.share.toFixed(1).replace('.', ',')}</td>
                <td style={{ ...td(true), color: (c.change_pct ?? 0) >= 0 ? T.greenText : T.redText }}>{pct(c.change_pct) || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function ProductsTable({ d }: { d: any }) {
  const { t } = useLang();
  return (
    <Card pad={22}>
      <div style={{ fontSize: 17, fontWeight: 800, color: T.text, marginBottom: 12 }}>{t('En Çok Gelir Getiren Ürünler')}</div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><Th>{t('Ürün')}</Th><Th right>{t('Adet')}</Th><Th right>{t('Ciro')}</Th><Th right>{t('Pay')}</Th></tr></thead>
          <tbody>
            {d.products.map((p: any) => (
              <tr key={p.code}>
                <td style={td()}><div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {p.image ? <img src={p.image} alt="" width={40} height={40} style={{ borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} /> : null}
                  <div style={{ minWidth: 0 }}><div style={{ fontWeight: 700 }}>{p.code}</div><div style={{ fontSize: 12, color: T.textMid, maxWidth: 480, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div></div></div></td>
                <td style={td(true)}>{p.qty}</td><td style={td(true)}>{tl(p.revenue)}</td>
                <td style={td(true)}><div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
                  <div style={{ width: 70, height: 6, borderRadius: 3, background: 'var(--py-track-bg)' }}><div style={{ width: `${Math.min(100, p.share * 4)}%`, height: 6, borderRadius: 3, background: T.accent }} /></div>%{p.share.toFixed(1).replace('.', ',')}</div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function Finance({ d, clientId, reload }: { d: any; clientId: number; reload: () => void }) {
  const { t } = useLang();
  const f = d.finance;
  const [rate, setRate] = useState<string>(f.cost_rate != null ? String(f.cost_rate) : '');
  const [saving, setSaving] = useState(false);
  const save = () => {
    const v = rate.trim() === '' ? null : Number(rate.replace(',', '.'));
    if (v !== null && !(v > 0 && v < 100)) return;
    setSaving(true);
    axios.put(`${API}/panolar/sales-board/cost?client_id=${clientId}`, { cost_rate: v }).then(reload).catch(() => {}).finally(() => setSaving(false));
  };
  return (
    <>
      <Grid min={230} gap={14}>
        <Kpi label={t('Toplam Ciro')} value={tl(f.revenue)} color="#3B82F6" icon={ICONS.revenue}><Delta v={d.overview.changes.revenue} suffix={t('vs. önceki dönem')} /></Kpi>
        <Kpi label={t('İade Edilen Ciro')} value={tl(f.returned_revenue)} color="#EF4444" icon={ICONS.orders}><div style={{ fontSize: 12.5, color: T.textMid }}>%{f.return_rate.toFixed(1).replace('.', ',')}{f.cancelled_revenue ? ` · ${t('İptal')} ${tl(f.cancelled_revenue)}` : ''}</div></Kpi>
        <Kpi label={t('Reklam Harcaması')} value={tl(f.ad_spend)} color="#8B5CF6" icon={ICONS.basket}><div style={{ fontSize: 12.5, color: T.textMid }}>{f.ad_share != null ? `${t('Reklam Payı')}: %${f.ad_share.toFixed(1).replace('.', ',')}` : '—'}</div></Kpi>
        <Kpi label={t('Toplam ROAS')} value={f.total_roas ? f.total_roas.toFixed(2) + 'x' : '—'} color="#10B981" icon={ICONS.revenue}><div style={{ fontSize: 12.5, color: T.textMid }}>{f.ad_platforms.map((p: any) => `${p.label} ${tlK(p.spend)}`).join(' · ') || '—'}</div></Kpi>
      </Grid>
      {f.net_revenue ? (
        <Card pad={22}>
          <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap' }}>
            {[[t('Net Ciro'), tl(f.net_revenue)], [t('Komisyon'), tl(f.commission)], [t('Kargo'), tl(f.shipping)]].map(([k, v]) => (
              <div key={k}><div style={{ fontSize: 11, color: T.textDim, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em' }}>{k}</div><div style={{ fontSize: 20, fontWeight: 800, fontFamily: MONO, color: T.text, marginTop: 4 }}>{v}</div></div>
            ))}
          </div>
        </Card>
      ) : null}
      <Card pad={22}>
        <div style={{ fontSize: 17, fontWeight: 800, color: T.text }}>{t('Ürün Maliyeti')}</div>
        <div style={{ fontSize: 13, color: T.textMid, margin: '4px 0 14px' }}>{t('Maliyet oranını gir, kâr marjı otomatik hesaplansın.')}</div>
        {f.cost_from_pixa ? null : <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <label style={{ fontSize: 13, color: T.textMid, fontWeight: 600 }}>{t('Ortalama ürün maliyeti (% ciro)')}</label>
          <input value={rate} onChange={e => setRate(e.target.value)} placeholder="örn. 45" inputMode="decimal"
            style={{ width: 90, padding: '8px 10px', borderRadius: 9, border: `1px solid ${T.border}`, background: 'var(--py-surface)', color: T.text, fontFamily: MONO, fontSize: 13 }} />
          <Button variant="solid" onClick={save} disabled={saving}>{t('Kaydet')}</Button>
        </div>}
        {f.margin != null && (
          <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', marginTop: 20, paddingTop: 16, borderTop: `1px solid ${T.border}` }}>
            {[[t('Ürün Maliyeti'), tl(f.product_cost)], [t('Brüt Kâr'), tl(f.gross_profit)], [t('Kâr Marjı'), `%${f.margin.toFixed(1).replace('.', ',')}`], [t('Reklam Sonrası Kâr'), tl(f.profit_after_ads)]].map(([k, v]) => (
              <div key={k}><div style={{ fontSize: 11, color: T.textDim, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em' }}>{k}</div><div style={{ fontSize: 20, fontWeight: 800, fontFamily: MONO, color: T.text, marginTop: 4 }}>{v}</div></div>
            ))}
          </div>
        )}
      </Card>
    </>
  );
}

export default function SalesBoard({ clientId, boardTitle, onBack }: { clientId: number; boardTitle: string; onBack: () => void }) {
  const { t } = useLang();
  const [tab, setTab] = useState<typeof TABS[number]>('Genel Bakış');
  const [d, setD] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [start, setStart] = useState(() => { const x = new Date(); x.setDate(x.getDate() - 29); return x.toISOString().slice(0, 10); });
  const [end, setEnd] = useState(() => new Date().toISOString().slice(0, 10));

  const load = () => {
    setLoading(true); setFailed(false);
    axios.get(`${API}/panolar/sales-board`, { params: { client_id: clientId, start_date: start, end_date: end } })
      .then(r => setD(r.data)).catch(() => setFailed(true)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [clientId, start, end]);

  const o = d?.overview;
  const empty = d && !o.orders && !d.covered_from;
  const channelKeys: string[] = d ? d.channels.map((c: any) => c.key) : [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={onBack} style={{ width: 32, height: 32, borderRadius: 9, border: `1px solid ${T.border}`, background: 'var(--py-surface)', cursor: 'pointer', color: T.textMid, fontSize: 15 }}>←</button>
          <div style={{ fontSize: 19, fontWeight: 800, letterSpacing: '-0.02em', color: T.text }}>{boardTitle}</div>
        </div>
        <DateFilterBox start={start} end={end} onChange={(s, e) => { setStart(s); setEnd(e); }} />
      </div>

      <div style={{ display: 'flex', background: 'var(--py-surface)', border: `1px solid ${T.border}`, borderRadius: 14, overflow: 'hidden' }}>
        {TABS.map(x => (
          <button key={x} onClick={() => setTab(x)} style={{ flex: 1, padding: '15px 10px', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 700, fontFamily: 'inherit',
            background: tab === x ? 'var(--py-track-bg)' : 'transparent', color: tab === x ? T.blueText : T.text }}>{t(x)}</button>
        ))}
      </div>

      {loading && !d ? <PoyaisSpinner size={36} pad={24} /> : failed && !d ? (
        <div style={{ fontSize: 13, color: T.textDim }}>{t('Veri alınamadı.')}</div>
      ) : empty ? (
        <div style={{ border: '1px dashed var(--py-border-soft)', borderRadius: 16, padding: '54px 22px', textAlign: 'center', fontSize: 13, fontWeight: 600, color: T.textMid }}>
          {t('Bu müşteri için sipariş verisi yok. Sipariş kaynağı (Pixa, Shopify ya da dosya) bağlanınca burada görünür.')}
        </div>
      ) : d && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18, opacity: loading ? 0.6 : 1 }}>
          {tab !== 'Finans' && (
            <Grid min={230} gap={14}>
              <Kpi label={t('Toplam Ciro')} value={tlK(o.revenue)} color="#3B82F6" icon={ICONS.revenue}><Delta v={o.changes.revenue} suffix={t('vs. önceki dönem')} /></Kpi>
              <Kpi label={t('Sipariş Adedi')} value={String(o.orders)} color="#22C55E" icon={ICONS.orders}><Delta v={o.changes.orders} suffix={t('vs. önceki dönem')} /></Kpi>
              <Kpi label={t('Ortalama Sepet')} value={tl2(o.avg_basket)} color="#A855F7" icon={ICONS.basket}><Delta v={o.changes.avg_basket} suffix={t('vs. önceki dönem')} /></Kpi>
              <Kpi label={t('Kâr Marjı')} value={o.margin != null ? `%${o.margin.toFixed(1).replace('.', ',')}` : '–'} color="#F97316" icon={ICONS.margin}>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.textMid }}>{o.margin != null ? '' : t('Ürün maliyeti girilince hesaplanır')}</div>
              </Kpi>
            </Grid>
          )}
          {tab === 'Genel Bakış' && <><TrendChart d={d} /><ChannelsTable d={d} /></>}
          {tab === 'Satışlar' && <><TrendChart d={d} channelKeys={channelKeys} /><ChannelsTable d={d} /></>}
          {tab === 'Ürünler' && <ProductsTable d={d} />}
          {tab === 'Finans' && <Finance d={d} clientId={clientId} reload={load} />}
          <div style={{ fontSize: 11.5, color: T.textDim }}>
            {d.source ? `${t('Veri kaynağı')}: ${d.source}` : ''}{d.updated_at ? ` · ${t('Son güncelleme')}: ${d.updated_at.slice(0, 16).replace('T', ' ')}` : ''}
          </div>
        </div>
      )}
    </div>
  );
}
